'use client';

import { c as _c } from "react/compiler-runtime";
import type { ConsumerBucket } from '@viveportsocial/device-capability-gate';
import { detectDevice } from '@viveportsocial/device-capability-gate';
import { useEffect, useState } from 'react';
import type { DeviceCapabilityTier } from '@/lib/cross-platform-gating';
import type { UASnapshot } from '@/server/lib/ua-snapshot';
export type MobileBrowserCapabilityStatus = 'idle' | 'detecting' | 'ready' | 'error';
export type MobileBrowserCapabilitySnapshot = {
  status: MobileBrowserCapabilityStatus;
  capabilityTier: DeviceCapabilityTier;
  consumerBucket: ConsumerBucket | undefined;
};

/**
 * Maps the library's consumer-facing MemoryTier to the app's DeviceCapabilityTier.
 *
 * Mapping rationale (conservative):
 * - constrained → constrained  (< 4 GB RAM)
 * - standard    → standard     (4–8 GB RAM)
 * - high-end    → highEnd      (≥ 8 GB RAM)
 * - unknown     → unknown      (no data → safe fallback)
 */
function memoryTierToCapabilityTier(memoryTier: ConsumerBucket['memory']): DeviceCapabilityTier {
  switch (memoryTier) {
    case 'constrained':
      return 'constrained';
    case 'standard':
      return 'standard';
    case 'high-end':
      return 'highEnd';
    default:
      return 'unknown';
  }
}
type UseMobileBrowserDeviceCapabilityOptions = {
  enabled: boolean;
  /** Detection only runs for mobile_browser; all other devices return idle/unknown. */
  device: UASnapshot['device'];
};
export function useMobileBrowserDeviceCapability(t0) {
  const $ = _c(5);
  const {
    enabled,
    device
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      status: "idle",
      capabilityTier: "unknown",
      consumerBucket: undefined
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [snapshot, setSnapshot] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== device || $[2] !== enabled) {
    t2 = () => {
      if (!enabled || device !== "mobile_browser") {
        return;
      }
      let cancelled = false;
      setSnapshot(_temp);
      detectDevice({
        consumerBucketOptions: {
          strategy: "conservative"
        }
      }).then(result => {
        if (cancelled) {
          return;
        }
        const capabilityTier = memoryTierToCapabilityTier(result.consumerBucket.memory);
        const next = {
          status: "ready",
          capabilityTier,
          consumerBucket: result.consumerBucket
        };
        setSnapshot(next);
      }).catch(() => {
        if (cancelled) {
          return;
        }
        setSnapshot({
          status: "error",
          capabilityTier: "unknown",
          consumerBucket: undefined
        });
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [enabled, device];
    $[1] = device;
    $[2] = enabled;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  return snapshot;
}
function _temp(s) {
  return {
    ...s,
    status: "detecting"
  };
}